import { View, CollectionView, ViewData, HttpClient, PathInfo, RegistryInterface } from "silver";
import { BookModalService } from "core/Services/BookModalService";
import { BookDataService } from "core/Services/BookDataService";
import { RecommendedLang } from "Lang/Pages/Library";
import { MyonLanguageLoader } from "Lang";

const HTML = `<div class="banner3d" aria-live="polite" role="region" aria-label="{{ lang.CAROUSEL_TITLE }}" aria-roledescription="carousel">
	<div class="g_screenReader">{{ lang.CAROUSEL_INSTRUCTIONS }}</div>
	<button type="button" class="banner3d_button -playpause" data-myon-name="playPauseButton" data-myon-click="handlePlayPauseButtonClick"></button>
	<button type="button" class="banner3d_arrow -left" aria-label="{{ lang.PREVIOUS_SLIDE }}" data-myon-click="handlePreviousButtonClick" aria-controls="banner-3d-slides">
	</button>
	<button type="button" class="banner3d_arrow -right" aria-label="{{ lang.NEXT_SLIDE }}" data-myon-click="handleNextButtonClick" aria-controls="banner-3d-slides">
	</button>
	<ul class="banner3d_thumbnailList">
		<li class="banner3d_thumbnailItem" data-myon-template="thumbnails">
			<button type="button" class="banner3d_button -thumbnail" aria-controls="banner-3d-slides" data-myon-name="button"></button>
		</li>
	</ul>
	<div class="banner3d_layers" id="banner-3d-slides" data-myon-name="layerContainer">
		<div class="banner3d_layer" data-myon-template="slides" aria-roledescription="slide" role="group"></div>
	</div>
</div>`;

const isButton = (e: HTMLElement): e is HTMLButtonElement => {
	return (e.tagName === "BUTTON");
};

interface Banner3dViewData extends ViewData {
	slides: Record<string, any>;
	thumbnails: Record<string, any>;
	lang: RecommendedLang;
}

interface Banner3dView extends View<Banner3dViewData> {
	playPauseButton: HTMLButtonElement;
	layerContainer: HTMLDivElement;
	slides: CollectionView<ViewData>;
	thumbnails: CollectionView<ViewData, { button: HTMLButtonElement } & View<ViewData>>;
}

function positionAtTime(positionString: string|undefined): (time: number) => number {
	let vRight: number, vLeft: number;
	if (!positionString) {
		vRight = vLeft = 0;
	} else if (positionString.indexOf(",") >= 0) {
		const values = positionString.split(",");
		vLeft = +values[ 0 ];
		vRight = +values[ 1 ];
	} else {
		vLeft = vRight = +(positionString);
	}
	return (time: number) => {
		if (time > 0) {
			return vLeft * time;
		}
		return vRight * time;
	};
}

interface PositionDriver {
	position(time: number): void;
}

/**
 * Positions a layer using margin styles
 * @param {HTMLElement} element
 */
export class MarginLayerPositionDriver implements PositionDriver {
	private readonly vy: (time: number) => number;
	private readonly vx: (time: number) => number;
	private readonly xMult: -1|1;
	private readonly yMult: -1|1;
	private readonly xMarg: "marginRight"|"marginLeft";
	private readonly yMarg: "marginTop"|"marginBottom";

	constructor(private element: HTMLElement) {
		this.vx    = positionAtTime(element.dataset.vx);
		this.vy    = positionAtTime(element.dataset.vy);

		this.xMult = (element.style.right ? -1 : 1);
		this.yMult = (element.style.bottom ? -1 : 1);

		this.xMarg = (element.style.right ? "marginRight" : "marginLeft");
		this.yMarg = (element.style.bottom ? "marginBottom" : "marginTop");
	}

	public position(time: number): void {
		this.element.style[ this.xMarg ] = (this.vx(time) * this.xMult) + "px";
		this.element.style[ this.yMarg ] = (this.vy(time) * this.yMult) + "px";
	}
}

/**
 * Positions a layer using transform styles.
 * @param {HTMLElement} element
 */
export class TransformLayerPositionDriver implements PositionDriver {

	private readonly vy: (time: number) => number;
	private readonly vx: (time: number) => number;

	constructor(private element: HTMLElement) {
		this.vx = positionAtTime(element.dataset.vx);
		this.vy = positionAtTime(element.dataset.vy);
	}

	public position(time: number): void {
		this.element.style.transform = "translate(" + this.vx(time) + "px, " + this.vy(time) + "px)";
	}
}

/**
 * Loader constructs a Banner3d element, and saturates when available
 */
export class Banner3dLoader {
	constructor(
		private bookModalService: BookModalService,
		private bookDataService: BookDataService,
		private httpClient: HttpClient,
		private userStorage: RegistryInterface<boolean>,
		private langLoader: MyonLanguageLoader,
		private manifestUrlBuilderFn: (bannerUrl: string) => string,
		private layerFactoryFn: LayerFactoryFunction,
		private imageUrlTransformer?: (imageUrl: string) => string
	) { }

	public async loadBanner(bannerRoot: string, bandType: number): Promise<Banner3d> {
		const lang = await this.langLoader.load("Pages\\Library\\Recommended");
		const control = new Banner3d(this.bookModalService, this.bookDataService, this.userStorage, lang, this.layerFactoryFn, bandType);
		this.httpClient.get(this.manifestUrlBuilderFn(bannerRoot), { decoding: "text/plain" })
			.then((html: string) => {
				const fromRegex = /src="(.*?)"/g;
				control.loadString(html.replace(fromRegex, (fullString: string, url: string): string => {
					if (this.imageUrlTransformer) {
						url = this.imageUrlTransformer(url);
					}
					const pathInfo = PathInfo.fromURL(url);
					if (pathInfo === null) {
						url = bannerRoot + url;
					}
					return `src="${url}"`;
				}));
			});
		return control;
	}
}

/**
 * Handles enabling and rendering of banner elements
 */
export class Banner3dLayer {
	private isEnabled: boolean = false;
	constructor(
		private element: HTMLElement,
		private positionDriver: PositionDriver
	) { }

	public renderAtTime(time: number): void {
		this.positionDriver.position(time);
	}

	public get enabled(): boolean {
		return this.isEnabled;
	}

	public set enabled(enabled: boolean) {
		this.isEnabled = enabled;
		if (this.element.dataset.bookId) {
			this.element.setAttribute("aria-hidden", enabled ? "false" : "true");
			this.element.setAttribute("tabindex", enabled ? "0" : "-1");
		}
	}
}

type LayerFactoryFunction = (layer: HTMLElement) => Banner3dLayer;

export class Banner3d {

	public static TransformLayerPositionDriver: typeof  TransformLayerPositionDriver = TransformLayerPositionDriver;
	public static MarginLayerPositionDriver: typeof  MarginLayerPositionDriver = MarginLayerPositionDriver;
	public static Layer: typeof Banner3dLayer = Banner3dLayer;
	public static Loader: typeof Banner3dLoader = Banner3dLoader;

	public static DURATION_ANIMATION = 1000; // How long to expect animation transition to take
	public static DURATION_DISPLAY   = 6000; // How long to wait before going to the next panel
	public static TRIGGER_RATIO      = 0.3333; // How far across the window (%) to trigger moving to the next page
	public static CONTROL_WIDTH      = 1200; // Expected max width of the control

	private panels: Array<{ panel: string, layers: Banner3dLayer[]}> = [];
	private panelIndex: number = -1;
	private panelToIndex: { [ panel: string]: number } = { };
	private focusIn: boolean = false;
	private mouseIn: boolean = false;
	private isReady: boolean = false;
	private timer: number = -1;
	private view: Banner3dView;

	/**
	 * Builds a 3D Banner for book previews
	 */
	constructor(
		private bookModalService: BookModalService,
		private bookDataService: BookDataService,
		private userStorage: RegistryInterface<boolean>,
		private lang: RecommendedLang,
		private layerFactoryFn: LayerFactoryFunction,
		private bandType: number
	) {
		this.view = new View<Banner3dViewData>(HTML, {
			handlePreviousButtonClick:  () => { this.goToPreviousPanel(); },
			handleNextButtonClick:      () => { this.goToNextPanel(); },
			handlePlayPauseButtonClick: () => { this.togglePlayPause(); }
		}, {
			slides:     { },
			thumbnails: { },
			lang
		}) as Banner3dView;
		this.initialize();
	}

	get autoPlay(): boolean {
		const value = this.userStorage.get("banner-3d-autoplay");
		if (value === undefined) {
			return true;
		}
		return value;
	}

	set autoPlay(value: boolean) {
		this.userStorage.set("banner-3d-autoplay", value);
		this.view.playPauseButton.classList.toggle("-paused", !value);
		this.view.layerContainer.setAttribute("aria-live", value ? "off" : "polite");
		if (value) {
			this.view.playPauseButton.setAttribute("aria-label", this.lang.CAROUSEL_STOP_AUTO());
			this.startTimer();
		} else {
			this.view.playPauseButton.setAttribute("aria-label", this.lang.CAROUSEL_START_AUTO());
			this.stopTimer();
		}
	}

	private togglePlayPause(): void {
		this.autoPlay = !this.autoPlay;
	}

	private renderPanelAtTime(panelIndex: number, time: number): void {
		this.panels[ panelIndex ].layers.forEach((layer) => {
			layer.renderAtTime(time);
		});
	}

	private renderAtProgress(leftPanelIndex: number, rightPanelIndex: number|null, progress: number): void {
		this.renderPanelAtTime(leftPanelIndex, -1 * progress);
		if (rightPanelIndex !== null) {
			this.renderPanelAtTime(rightPanelIndex, 1 - progress);
		}
	}

	private togglePanelActive(panelIndex: number, value: boolean): void {
		const thumbnail = this.view.thumbnails.get(String(panelIndex)).button;
		thumbnail.classList.toggle("-current", value);
		thumbnail.setAttribute("aria-current", value ? "true" : "false");

		const slide = this.view.slides.get(String(panelIndex)).element;
		slide.setAttribute("aria-hidden", value ? "false" : "true");

		this.panels[ panelIndex ].layers.forEach((layer) => {
			layer.enabled = value;
		});
	}

	private goToPanelImmediately(panelIndex: number): void {
		this.view.layerContainer.classList.remove("-transitioning", "ready");
		this.panels.forEach((panel, i) => {
			this.renderPanelAtTime(i, panelIndex == i ? 0 : 1);
			this.togglePanelActive(i, panelIndex == i);
		});
		this.panelIndex = panelIndex;
		this.isReady = true;
		setTimeout(() => {
			this.view.layerContainer.classList.add("-ready");
		}, 1);
		this.startTimer();
	}

	public loadString(html: string): void {
		this.view.layerContainer.innerHTML = html;
		const layers = Array.from(this.view.layerContainer.querySelectorAll("[data-panel]")) as HTMLElement[];
		layers.forEach((layerEl: HTMLElement) => {
			const panel = layerEl.dataset.panel as string;
			if (!this.panelToIndex.hasOwnProperty(panel)) {
				const i = this.panels.length;
				this.panelToIndex[ panel ] = i;
				this.view.slides.add(String(i), { });
				this.view.thumbnails.add(String(i) , { });
				this.panels.push({ panel, layers: [] });
			}

			const layer = this.layerFactoryFn(layerEl);
			const index = this.panelToIndex[ panel ];

			this.panels[index].layers.push(layer);

			this.view.slides.get(String(index)).element.appendChild(layerEl);
		});

		const slideCount = this.panels.length;
		this.view.slides.forEach((slide, index) => {
			slide.element.setAttribute("aria-label", this.lang.SLIDE_N(Number(index) + 1, slideCount));
		});
		this.view.thumbnails.forEach((thumbnail, index) => {
			thumbnail.button.addEventListener("click", () => {
				this.goToPanel(Number(index), undefined);
			});
			thumbnail.button.setAttribute("aria-label", this.lang.GOTO_SLIDE_N(Number(index) + 1, slideCount));
		});


		if (this.panels.length === 1) {
			this.view.element.classList.add("-no-scroll");
		}

		const bookElements = Array.from(this.view.layerContainer.querySelectorAll("[data-book-id]")) as HTMLElement[];
		const bookData = this.bookDataService.getFromIds(bookElements.map(el => Number(el.dataset.bookId as string)));
		bookElements.forEach(el => {
			const bookId = Number(el.dataset.bookId as string);
			el.addEventListener("click", () => {
				this.bookModalService.showBookModal(bookId, {
					source:      el,
					callingBand: this.bandType
				}).catch(console.error);
			});
			bookData.then((byId) => {
				const book = byId[ bookId ];
				if (book) {
					Array.from(el.querySelectorAll("img")).forEach(img => {
						img.setAttribute("alt", this.lang.ALT_BOOK_COVER(book.title));
					});
					el.setAttribute("aria-label", this.lang.SELECT_BOOK(book.title));
				}
			});
			return bookId;
		});

		this.goToPanelImmediately(0);
	}

	private getNextPanelIndex(): number {
		return (this.panelIndex + 1) % this.panels.length;
	}

	private getPreviousPanelIndex(): number {
		return (this.panelIndex + this.panels.length - 1) % this.panels.length;
	}

	public goToNextPanel(): void {
		this.goToPanel(this.getNextPanelIndex(), 1);
	}

	public goToPreviousPanel(): void {
		this.goToPanel(this.getPreviousPanelIndex(), -1);
	}

	private startTimer(): void {
		if (this.timer !== -1 || !this.autoPlay || this.focusIn || this.mouseIn) {
			return;
		}
		this.timer = setInterval(() => {
			this.goToNextPanel();
		}, Banner3d.DURATION_DISPLAY);
	}

	private stopTimer(): void {
		if (this.timer === -1) {
			return;
		}
		clearTimeout(this.timer);
		this.timer = -1;
	}

	public goToPanel(panelIndex: number, directionP?: 1|-1): void {
		if (!this.isReady || this.panelIndex === panelIndex) {
			return;
		}
		const  direction: -1|1 = (directionP === undefined) ? ((this.panelIndex < panelIndex) ? 1 : -1) : directionP;
		this.isReady = false;
		this.view.layerContainer.classList.remove("-transitioning", "-ready");
		this.togglePanelActive(this.panelIndex, false);
		this.togglePanelActive(panelIndex, true);
		if (direction === 1) {
			this.renderAtProgress(this.panelIndex, panelIndex, 0);
		} else {
			this.renderAtProgress(panelIndex, this.panelIndex, 1);
		}
		setTimeout(() => {
			this.view.layerContainer.classList.add("-transitioning");
			if (direction === 1) {
				this.renderAtProgress(this.panelIndex, panelIndex, 1);
			} else {
				this.renderAtProgress(panelIndex, this.panelIndex, 0);
			}
			this.panelIndex = panelIndex;
			setTimeout(() => {
				this.view.layerContainer.classList.remove("-transitioning");
				this.view.layerContainer.classList.add("-ready");
				this.isReady = true;
			}, Banner3d.DURATION_ANIMATION);
		}, 20);
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	private initialize(): void {
		this.view.layerContainer.addEventListener("mouseenter", () => {
			this.mouseIn = true;
			this.stopTimer();
		});
		this.view.layerContainer.addEventListener("mouseleave", () => {
			this.mouseIn = false;
			this.startTimer();
		});
		document.documentElement.addEventListener("focusin", (e: FocusEvent) => {
			if (e.target instanceof Node && this.view.layerContainer.contains(e.target)) {
				this.focusIn = true;
				this.stopTimer();
			} else {
				this.focusIn = false;
				this.startTimer();
			}
		});

		let anchor: MouseEvent|undefined;
		this.view.layerContainer.addEventListener("mousedown", (e: MouseEvent) => {
			const target = e.target;
			if (!this.isReady || !target) {
				return;
			}
			if (isButton(target as HTMLElement)) {
				return;
			}
			anchor = e;
			this.togglePanelActive(this.panelIndex, false);
			this.view.layerContainer.classList.remove("-transitioning", "-ready");
			this.renderAtProgress(this.panelIndex, null, 0);
			this.stopTimer();
			this.isReady = false;
			e.preventDefault();
		});

		const handleMouseMove = (e: MouseEvent) => {
			if (! anchor) {
				return;
			}
			const progress = (anchor.pageX - e.pageX) / Banner3d.CONTROL_WIDTH;
			if (progress > 0) {
				this.renderAtProgress(this.panelIndex, this.getNextPanelIndex(), progress);
			} else {
				this.renderAtProgress(this.getPreviousPanelIndex(), this.panelIndex, 1 + progress);
			}
			e.preventDefault();
		};
		document.body.addEventListener("mousemove", handleMouseMove);

		const handleMouseUp = (e: MouseEvent) => {
			if (! anchor) {
				return;
			}
			const delta = (anchor.pageX - e.pageX);
			const progress = delta / Banner3d.CONTROL_WIDTH;

			this.view.layerContainer.classList.add("-dragTransitioning");
			setTimeout(() => {
				const triggers = Math.abs(delta) > (Math.min(Banner3d.CONTROL_WIDTH, document.documentElement.clientWidth * Banner3d.TRIGGER_RATIO));
				if (progress > 0) {
					this.renderAtProgress(this.panelIndex, this.getNextPanelIndex(), triggers ? 1 : 0);
				} else {
					this.renderAtProgress(this.getPreviousPanelIndex(), this.panelIndex, triggers ? 0 : 1);
				}
				if (triggers) {
					this.panelIndex = (progress > 0 ? this.getNextPanelIndex() : this.getPreviousPanelIndex());
				}

				setTimeout(() => {
					this.togglePanelActive(this.panelIndex, true);

					this.view.layerContainer.classList.remove("-dragTransitioning");
					this.view.layerContainer.classList.add("-ready");
					this.isReady = true;

				}, 300);
			}, 10);
			anchor = undefined;
			e.preventDefault();
		};

		document.body.addEventListener("mouseup", handleMouseUp);
		document.body.addEventListener("mouseleave", handleMouseUp);

		window.addEventListener("resize", () => {
			this.handleResize();
		});

		// eslint-disable-next-line
		this.autoPlay = this.autoPlay;
		this.handleResize();
	}

	private handleResize(): void {
		if (!this.view.element.offsetWidth) {
			setTimeout(() => this.handleResize(), 30);
		}
		const ratio = Math.min(1, this.view.element.offsetWidth / Banner3d.CONTROL_WIDTH);
		this.view.layerContainer.style.transform = `scale(${ratio}, ${ratio})`;
	}
}
